import { useCallback, useEffect, useState } from "react";
import { Plus, MapPin, Pencil, Trash2, Image as ImageIcon, QrCode } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PlanMap } from "../components/PlanMap";
import { toast } from "sonner";
import { api, fmtErr, fmtDate, fileUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSite } from "../lib/site";
import { PageHeader } from "../components/ModulePage";
import { FormDialog } from "../components/FormDialog";
import { AreaDetail } from "../components/AreaDetail";
import { PhotoUpload, Thumb } from "../components/PhotoUpload";
import { RagBadge } from "../components/Badges";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const AREA_FIELDS = [
  { name: "name", label: "Area name", type: "text", required: true },
  { name: "code", label: "Area code (blank = auto)", type: "text" },
  { name: "order", label: "Display order", type: "number" },
  { name: "description", label: "Description / boundaries", type: "textarea" },
  { name: "photos", label: "Area photographs", type: "photos" },
];

const Areas = ({ areas, reload }) => {
  const { isManager } = useAuth();
  const [edit, setEdit] = useState(null);
  const [sel, setSel] = useState(null);
  const nav = useNavigate();
  const save = async (v) => {
    try {
      if (v.id) await api.put(`/areas/${v.id}`, v);
      else await api.post("/areas", v);
      toast.success("Area saved");
      setSel(null);
      reload();
    } catch (e) { toast.error(fmtErr(e)); throw e; }
  };
  const del = async () => {
    if (!window.confirm(`Delete area ${sel.name}? Assets stay in their registers.`)) return;
    await api.delete(`/areas/${sel.id}`);
    setSel(null);
    reload();
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {isManager && <Button onClick={() => setEdit({})} className="tap bg-rose-600 hover:bg-rose-700" data-testid="add-area-btn"><Plus className="mr-1 h-4 w-4" />Add area</Button>}
        {areas.length > 0 && <Button variant="outline" onClick={() => nav("/area-labels")} className="tap border-slate-700 bg-transparent" data-testid="print-all-area-qr-btn"><QrCode className="mr-1 h-4 w-4" />Print area QR labels</Button>}
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {areas.map((a) => (
          <button key={a.id} onClick={() => setSel(a)} className="panel overflow-hidden text-left transition-colors hover:border-slate-600" data-testid={`area-card-${a.code}`}>
            {a.photos?.[0] ? <img src={fileUrl(a.photos[0])} alt={a.name} className="h-32 w-full object-cover" />
              : <div className="flex h-32 items-center justify-center bg-slate-900 text-slate-700"><ImageIcon className="h-8 w-8" /></div>}
            <div className="p-4">
              <p className="font-mono text-xs text-cyan-300">{a.code}</p>
              <p className="font-display text-xl font-bold uppercase">{a.name}</p>
              <div className="mt-2 flex items-center justify-between text-xs text-slate-400">
                <span>{a.asset_count} asset(s)</span>
                {a.last_audit_at ? <RagBadge status={a.last_audit_fails ? "red" : "green"} label={`Audited ${fmtDate(a.last_audit_at)}`} /> : <RagBadge status="amber" label="Not audited" />}
              </div>
            </div>
          </button>
        ))}
        {!areas.length && <p className="panel col-span-full p-8 text-center text-sm text-slate-500">No areas yet. Add areas like Goods In, Aisle 1-10, Workshop, Mill Hall.</p>}
      </div>
      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit?.id ? "Edit area" : "New area"} fields={AREA_FIELDS} initial={edit} onSubmit={save} testid="area-form" />
      <AreaDetail area={sel} onClose={() => setSel(null)} onEdit={() => setEdit(sel)} onDelete={del} />
    </div>
  );
};

const isImg = (p) => !p.toLowerCase().endsWith(".pdf");

const PinEditor = ({ plans, areas, reload }) => {
  const [plan, setPlan] = useState(plans.find(isImg) || "");
  const [areaId, setAreaId] = useState("");
  useEffect(() => {
    if (!plans.includes(plan)) setPlan(plans.find(isImg) || "");
  }, [plans, plan]);
  if (!plan) return <p className="text-sm text-slate-500">Upload an image site plan (JPG/PNG) to pin areas.</p>;
  const place = async (x, y) => {
    if (!areaId) return toast.error("Select an area to pin first");
    try {
      await api.put(`/areas/${areaId}`, { pin: { plan, x, y } });
      toast.success("Area pinned");
      reload();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const unpin = async () => {
    await api.put(`/areas/${areaId}`, { pin: null });
    reload();
  };
  const pins = areas.filter((a) => a.pin?.plan === plan).map((a) => ({ id: a.id, code: a.code, name: a.name, label: a.code.replace(/^AREA-/, ""), x: a.pin.x, y: a.pin.y, state: a.id === areaId ? "current" : "done" }));
  return (
    <div className="panel space-y-3 p-4" data-testid="pin-editor">
      <p className="eyebrow">Pin areas on the plan - choose an area, then tap its location</p>
      <div className="flex flex-wrap gap-2">
        {plans.filter(isImg).length > 1 && (
          <select className="field max-w-xs" value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="pin-plan-select">
            {plans.filter(isImg).map((p, i) => <option key={p} value={p}>Plan {i + 1}</option>)}
          </select>
        )}
        <select className="field max-w-xs" value={areaId} onChange={(e) => setAreaId(e.target.value)} data-testid="pin-area-select">
          <option value="">Select area to pin...</option>
          {areas.map((a) => <option key={a.id} value={a.id}>{a.code} - {a.name}{a.pin ? " (pinned)" : ""}</option>)}
        </select>
        {areaId && areas.find((a) => a.id === areaId)?.pin && <Button variant="ghost" className="h-11 text-red-300" onClick={unpin} data-testid="unpin-area-btn">Remove pin</Button>}
      </div>
      <PlanMap plan={plan} pins={pins} onPlace={place} onPinClick={(p) => setAreaId(p.id)} testid="pin-editor-map" />
    </div>
  );
};

const Plans = ({ areas, reloadAreas }) => {
  const { site, reload } = useSite();
  const { isManager } = useAuth();
  const save = async (plans) => {
    try {
      await api.put(`/sites/${site.id}/plans`, { plans });
      toast.success("Site plans saved");
      reload();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const plans = site?.plans || [];
  return (
    <div className="space-y-4" data-testid="site-plans">
      <div className="grid gap-4 md:grid-cols-2">
        {plans.map((p) => (
          p.toLowerCase().endsWith(".pdf") ? <Thumb key={p} src={p} /> : (
            <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer" className="panel block overflow-hidden"><img src={fileUrl(p)} alt="Site plan" className="max-h-[420px] w-full object-contain bg-white" /></a>
          )
        ))}
        {!plans.length && <p className="panel col-span-full p-8 text-center text-sm text-slate-500">No site plans uploaded yet.</p>}
      </div>
      {isManager && (
        <div className="panel p-4">
          <p className="eyebrow mb-2">Upload / remove site plans (images or PDF)</p>
          <PhotoUpload value={plans} onChange={save} accept="image/*,application/pdf" label="Add plan" testid="site-plan-upload" />
        </div>
      )}
      {isManager && <PinEditor plans={plans} areas={areas} reload={reloadAreas} />}
    </div>
  );
};

const Pathways = () => {
  const { isManager } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/pathways").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const del = async (p) => {
    if (!window.confirm(`Delete pathway ${p.name}?`)) return;
    await api.delete(`/pathways/${p.id}`);
    load();
  };
  return (
    <div className="space-y-3">
      {isManager && <Button onClick={() => nav("/pathway-builder")} className="tap bg-rose-600 hover:bg-rose-700" data-testid="add-pathway-btn"><Plus className="mr-1 h-4 w-4" />Build a pathway (guided)</Button>}
      {rows.map((p) => (
        <div key={p.id} className="panel flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`pathway-row-${p.id}`}>
          <div>
            <p className="font-semibold">{p.name} <span className="text-xs text-slate-500">every {p.frequency_days} day(s)</span></p>
            <p className="mt-1 flex flex-wrap items-center gap-1 text-xs text-slate-400"><MapPin className="h-3 w-3" />{p.area_names.join(" → ")}</p>
          </div>
          {isManager && (
            <div className="flex gap-1">
              <Button size="icon" variant="ghost" onClick={() => nav(`/pathway-builder/${p.id}`)} data-testid={`edit-pathway-${p.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="text-red-300" onClick={() => del(p)} data-testid={`delete-pathway-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          )}
        </div>
      ))}
      {!rows.length && <p className="panel p-8 text-center text-sm text-slate-500">No pathways yet. Use the guided builder to create one.</p>}
    </div>
  );
};

export default function SiteSetup() {
  const { site } = useSite();
  const [areas, setAreas] = useState([]);
  const load = useCallback(() => api.get("/areas").then((r) => setAreas(r.data)), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <PageHeader eyebrow={site?.name} title="Site Setup" description="Define areas (with photos), upload site plans, then build pathway audit routes from area to area. Assign assets to areas from any register." />
      <Tabs defaultValue="areas">
        <TabsList className="mb-4 h-auto flex-wrap justify-start bg-slate-900">
          <TabsTrigger value="areas" className="h-10 data-[state=active]:bg-slate-700" data-testid="tab-areas">Areas</TabsTrigger>
          <TabsTrigger value="plans" className="h-10 data-[state=active]:bg-slate-700" data-testid="tab-plans">Site plans</TabsTrigger>
          <TabsTrigger value="pathways" className="h-10 data-[state=active]:bg-slate-700" data-testid="tab-pathways">Pathways</TabsTrigger>
        </TabsList>
        <TabsContent value="areas"><Areas areas={areas} reload={load} /></TabsContent>
        <TabsContent value="plans"><Plans areas={areas} reloadAreas={load} /></TabsContent>
        <TabsContent value="pathways"><Pathways /></TabsContent>
      </Tabs>
    </div>
  );
}
